import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { saveInterviewFeedback } from "../Store/Thunk";

const StarRating = ({ rating, onRatingChange, questionIndex, readOnly = false }) => {
  const [hoveredStar, setHoveredStar] = useState(0);

  return (
    <div className="d-flex gap-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <div
          key={star}
          style={{
            cursor: readOnly ? "default" : "pointer",
            fontSize: "28px",
            color: star <= (hoveredStar || rating) ? "#FFD700" : "#E7ECEF",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={() => !readOnly && setHoveredStar(star)}
          onMouseLeave={() => !readOnly && setHoveredStar(0)}
          onClick={() => !readOnly && onRatingChange(questionIndex, star)}
        >
          ★
        </div>
      ))}
    </div>
  );
};

const InterviewFeedbackModal = ({ show, onHide, candidateId, candidateName }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const interviewFeedback = useSelector((state) => state.candidate.interviewFeedback);

  const [ratings, setRatings] = useState({
    technicalSkills: 0,
    communication: 0,
    problemSolving: 0,
    culturalFit: 0,
  });

  const [additionalComments, setAdditionalComments] = useState("");
  const [currentUserFeedback, setCurrentUserFeedback] = useState(null);
  const [otherUsersFeedback, setOtherUsersFeedback] = useState([]);

  const questions = [
    {
      key: "technicalSkills",
      label: t("interviewFeedbackTechnicalSkills"),
    },
    {
      key: "communication",
      label: t("interviewFeedbackCommunication"),
    },
    {
      key: "problemSolving",
      label: t("interviewFeedbackProblemSolving"),
    },
    {
      key: "culturalFit",
      label: t("interviewFeedbackCulturalFit"),
    },
  ];

  // Load existing feedback when modal opens or feedback data changes
  useEffect(() => {
    if (show && interviewFeedback && Array.isArray(interviewFeedback)) {
      // Separate current user's feedback from others
      const currentFeedback = interviewFeedback.find(
        (feedback) => {
          const feedbackData = JSON.parse(feedback.feedbackJsonString);
          // Use evaluatorUserId from backend if userId is not in JSON (for backwards compatibility)
          const feedbackUserId = feedbackData.evaluator.userId || feedback.evaluatorUserId;
          return feedbackUserId === currentUser.userId;
        }
      );

      const othersFeedback = interviewFeedback.filter(
        (feedback) => {
          const feedbackData = JSON.parse(feedback.feedbackJsonString);
          // Use evaluatorUserId from backend if userId is not in JSON (for backwards compatibility)
          const feedbackUserId = feedbackData.evaluator.userId || feedback.evaluatorUserId;
          return feedbackUserId !== currentUser.userId;
        }
      );

      setCurrentUserFeedback(currentFeedback);
      setOtherUsersFeedback(othersFeedback);

      // If current user has feedback, load it into the form
      if (currentFeedback) {
        const feedbackData = JSON.parse(currentFeedback.feedbackJsonString);
        const loadedRatings = {};
        feedbackData.questions.forEach((q) => {
          loadedRatings[q.key] = q.rating;
        });
        setRatings(loadedRatings);
        setAdditionalComments(feedbackData.additionalComments || "");
      } else {
        // Reset form if current user has no feedback
        setRatings({
          technicalSkills: 0,
          communication: 0,
          problemSolving: 0,
          culturalFit: 0,
        });
        setAdditionalComments("");
      }
    }
  }, [show, interviewFeedback, currentUser.userId]);

  const handleRatingChange = (questionKey, value) => {
    setRatings((prev) => ({
      ...prev,
      [questionKey]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const currentTimestamp = new Date().toISOString();

    // Prepare the feedback JSON object
    const feedbackJson = {
      evaluator: {
        userId: currentUser.userId,
        userName: currentUser.userName || currentUser.name,
        email: currentUser.email,
      },
      questions: [
        {
          key: "technicalSkills",
          question: t("interviewFeedbackTechnicalSkills"),
          rating: ratings.technicalSkills,
        },
        {
          key: "communication",
          question: t("interviewFeedbackCommunication"),
          rating: ratings.communication,
        },
        {
          key: "problemSolving",
          question: t("interviewFeedbackProblemSolving"),
          rating: ratings.problemSolving,
        },
        {
          key: "culturalFit",
          question: t("interviewFeedbackCulturalFit"),
          rating: ratings.culturalFit,
        },
      ],
      additionalComments: additionalComments,
      submittedAt: currentTimestamp,
      updatedAt: currentTimestamp,
    };

    // Send candidateId and feedbackJsonString to backend
    const payload = {
      candidateId,
      feedbackJsonString: JSON.stringify(feedbackJson),
    };

    dispatch(saveInterviewFeedback(payload));

    handleClose();
  };

  const handleClose = () => {
    setRatings({
      technicalSkills: 0,
      communication: 0,
      problemSolving: 0,
      culturalFit: 0,
    });
    setAdditionalComments("");
    setCurrentUserFeedback(null);
    setOtherUsersFeedback([]);
    onHide();
  };

  const isFormValid = () => {
    return Object.values(ratings).every((rating) => rating > 0);
  };

  const calculateOverallRating = () => {
    if (!interviewFeedback || !Array.isArray(interviewFeedback) || interviewFeedback.length === 0) return null;
    const allRatings = interviewFeedback.flatMap(feedback => {
      const feedbackData = JSON.parse(feedback.feedbackJsonString);
      return feedbackData.questions.map(q => q.rating);
    });
    return (allRatings.reduce((sum, rating) => sum + rating, 0) / allRatings.length).toFixed(1);
  };

  const overallRating = calculateOverallRating();

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title className="text-18 text-color-gray-dark fw-semibold mb-0">
            {t("interviewFeedbackTitle")} - {candidateName}
          </Modal.Title>
          {overallRating && (
            <div className="d-flex align-items-center gap-1" style={{ backgroundColor: "#FFD700", padding: "4px 12px", borderRadius: "16px" }}>
              <span className="text-14 fw-bold" style={{ color: "#fff" }}>
                {overallRating}
              </span>
              <span style={{ color: "#fff", fontSize: "16px" }}>⭐</span>
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Display other users' feedback */}
        {otherUsersFeedback.length > 0 && (
          <div className="mb-2">
            <h6 className="text-12 text-color-gray-dark fw-semibold mb-1">
              {t("otherEvaluations") || "Other Evaluations"}
            </h6>
            {otherUsersFeedback.map((feedback, index) => {
              const feedbackData = JSON.parse(feedback.feedbackJsonString);
              const averageRating = (
                feedbackData.questions.reduce((sum, q) => sum + q.rating, 0) / feedbackData.questions.length
              ).toFixed(1);
              return (
                <Card key={index} className="mb-1 px-2 py-1" style={{ backgroundColor: "#F8F9FA", fontSize: "10px" }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-10 text-color-gray-dark fw-medium">
                        {feedback.evaluatorName} ({feedback.evaluatorEmail})
                      </span>
                      <span className="text-10 fw-bold" style={{ color: "#FFD700" }}>
                        ⭐ {averageRating}
                      </span>
                    </div>
                    <span className="text-10 text-color-gray-light" style={{ fontSize: "9px" }}>
                      {new Date(feedbackData.submittedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {feedbackData.questions.map((q) => (
                      <div key={q.key} className="d-flex align-items-center" style={{ fontSize: "10px", minWidth: "45%" }}>
                        <span className="text-10 text-color-gray-dark me-1" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "60px" }}>
                          {q.question.split(' ')[0]}:
                        </span>
                        <div className="d-flex" style={{ gap: "2px" }}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              style={{
                                fontSize: "12px",
                                color: star <= q.rating ? "#FFD700" : "#E7ECEF",
                                lineHeight: "1",
                              }}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {feedbackData.additionalComments && (
                    <div className="mt-1" style={{ borderTop: "1px solid #E7ECEF", paddingTop: "4px" }}>
                      <p className="text-10 text-color-gray-dark mb-0" style={{ fontSize: "10px" }}>
                        {feedbackData.additionalComments}
                      </p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Current user's feedback form */}
        <div className="mb-3">
          <h6 className="text-16 text-color-gray-dark fw-semibold">
            {currentUserFeedback ? t("yourEvaluation") || "Your Evaluation" : t("newEvaluation") || "New Evaluation"}
          </h6>
        </div>

        <Form onSubmit={handleSubmit}>
          {questions.map((question, index) => (
            <Row key={question.key} className="mb-4">
              <Col xs={12}>
                <Form.Label className="text-16 text-color-gray-dark fw-medium mb-2">
                  {question.label}
                </Form.Label>
              </Col>
              <Col xs={12}>
                <StarRating
                  rating={ratings[question.key]}
                  onRatingChange={(_, value) => handleRatingChange(question.key, value)}
                  questionIndex={question.key}
                />
              </Col>
            </Row>
          ))}

          <Row className="mb-3">
            <Col xs={12}>
              <Form.Label className="text-16 text-color-gray-dark fw-medium mb-2">
                {t("interviewFeedbackAdditionalComments")}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder={t("interviewFeedbackCommentsPlaceholder")}
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                className="default-text-input"
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} className="button secondary">
          {t("close")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isFormValid()}
          className="button primary"
        >
          {t("submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InterviewFeedbackModal;
